// packages/web-ui/src/panels/chat-panel/MessageBubble.tsx
//
// W2-S5 AC #4 + AC #5 — Single message bubble. Handles user / assistant /
// system styling, sysml-fence stripping (FR-CHAT-12), markdown rendering
// via `marked` + `DOMPurify`, and the streaming cursor when isStreaming.
// W2-S6-integration — mounts the per-message inline cards in the
// `inline-cards-slot` placeholder. The bubble stays decoupled from the
// store: callers (ChatPanel / MessageList) read envelopes via
// `selectCardsForMessage(messageId)` and pass them in alongside a composed
// `InlineCardHandlers` object. Tests cover the bubble with hand-built
// envelopes + handlers; the panel-level integration is exercised by the
// `multi-turn` and `streaming-chat` web-e2e specs.

import { useCallback, useMemo, type JSX, type KeyboardEvent, type MouseEvent } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Trash2 } from 'lucide-react';
import type { ChatPanelMessage } from '../../state/chat.js';
import type { InlineCardEnvelope } from '../../state/inline-cards.js';
import {
  InlineCardSlot,
  InlineCardErrorBoundary,
  type InlineCardHandlers,
} from '../../components/inline-cards/index.js';
import { stripSysmlFences } from './markdown-stream-state.js';
import { rewriteKbSourceCitations } from './rewrite-kb-citations.js';
import { ToolCallGroup } from './ToolCallGroup.js';

const ALLOWED_TAGS = [
  'p',
  'a',
  'b',
  'strong',
  'i',
  'em',
  'ul',
  'ol',
  'li',
  'pre',
  'code',
  'br',
  'hr',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'span',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'colgroup',
  'col',
];
const ALLOWED_ATTR = [
  'href',
  'target',
  'rel',
  'class',
  'title',
  'tabindex',
  'role',
  'aria-label',
  'part',
  'data-file',
  'data-element',
  'align',
];

export interface MessageBubbleProps {
  message: ChatPanelMessage;
  isStreaming: boolean;
  /** Test seam — override the slice's retry/rerun. */
  onRetryToolCall?: (callId: string) => void;
  onRerunToolCall?: (callId: string) => void;
  /** Test seam for opening syside IDE. */
  openInSyside?: (path: string) => void;
  /**
   * Click handler for KB source citation pills inside the assistant body.
   * Fired when the user clicks (or activates with Enter/Space on) any
   * `[data-file]` span produced by `rewriteKbSourceCitations`. `ChatPanel`
   * binds this to `useOpenResource('files', filePath)` so the source
   * `.sysml` file opens in a new SPA tab.
   */
  onOpenSource?: (filePath: string, elementName?: string) => void;
  /**
   * W2-S6-integration — inline-card envelopes attached to this assistant
   * message (read from the inline-cards slice via
   * `selectCardsForMessage(message.id)`). Empty / undefined means the
   * `inline-cards-slot` renders as an empty placeholder for backwards
   * compatibility with the W2-S5 AC #4 layout test.
   */
  inlineCards?: InlineCardEnvelope[];
  /**
   * W2-S6-integration — composed handlers for the four inline-card kinds.
   * Threaded straight through to each `<InlineCardSlot>` that is rendered.
   * Required when `inlineCards` is non-empty; ignored otherwise.
   */
  inlineCardHandlers?: InlineCardHandlers;
  /** Honour `prefers-reduced-motion: reduce`. Forwarded to inline-card slots. */
  reducedMotion?: boolean;
  /**
   * Hover-reveal delete handler. When supplied (and the message is not
   * mid-stream), a trash icon button is rendered in the top-right corner
   * of the bubble. Click fires this with the message id; the caller is
   * responsible for confirmation + server round-trip + local removal.
   * System messages render no delete affordance regardless.
   */
  onDelete?: (messageId: string) => void;
}

/**
 * Order inline cards inside a single assistant message bubble. The
 * `changeset-preview` envelope is always rendered FIRST (it is the
 * primary visual answer for KB proposal turns) so the user sees the
 * color-coded diagram above the per-entity accept/reject tree. All other
 * envelope kinds keep their original arrival order.
 *
 * Pure function — caller passes the slice's append-only list; we return a
 * shallow-sorted copy so React's reconciler can still rely on stable
 * identity for unchanged cards (`Array.prototype.sort` is stable across
 * V8 / JavaScriptCore for equal sort keys).
 */
function sortInlineCards(cards: InlineCardEnvelope[]): InlineCardEnvelope[] {
  const priority: Record<InlineCardEnvelope['kind'], number> = {
    'changeset-preview': 0,
    'response-view-proposal': 1,
    // IV3-S1 — raster card sorts alongside the response-view proposal (both
    // are visualization cards). Required key so the exhaustive Record stays
    // total after the `illustrative-image` union member was added.
    'illustrative-image': 1.5,
    // Deterministic connections table sorts just after the visualization cards
    // — the diagram is the visual, this is the authoritative complete data.
    'entity-connections': 1.8,
    // Generic deterministic data table sorts alongside the connections table.
    'data-table': 1.9,
    'kb-change-tree-shell': 2,
    'clarification': 3,
    'file-upload-confirm': 4,
  };
  return [...cards].sort((a, b) => priority[a.kind] - priority[b.kind]);
}

function renderMarkdown(content: string): string {
  const stripped = stripSysmlFences(content);
  // Unwrap any echoed `<span class="… kb-source-link …">` markup the LLM
  // sometimes reproduces from prior turns, then re-pill any plain citations
  // (`[Source: …]`, bare `path.sysml#Name`, …) into clickable spans. Runs
  // BEFORE `marked.parse(...)` — marked passes inline HTML through unchanged,
  // and DOMPurify's allowlist (above) keeps the pill attributes intact.
  const withCitations = rewriteKbSourceCitations(stripped);
  const raw = marked.parse(withCitations, { gfm: true, breaks: true, async: false });
  if (typeof raw !== 'string') return '';
  return DOMPurify.sanitize(raw, { ALLOWED_TAGS, ALLOWED_ATTR });
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts;
    return d.toTimeString().slice(0, 5);
  } catch {
    return ts;
  }
}

export function MessageBubble({
  message,
  isStreaming,
  onRetryToolCall,
  onRerunToolCall,
  openInSyside,
  onOpenSource,
  inlineCards,
  inlineCardHandlers,
  reducedMotion,
  onDelete,
}: MessageBubbleProps): JSX.Element {
  const time = formatTimestamp(message.timestamp);
  const handleDelete = useCallback(() => {
    if (onDelete === undefined) return;
    onDelete(message.id);
  }, [onDelete, message.id]);
  const deleteAffordanceVisible =
    onDelete !== undefined && !isStreaming && message.role !== 'system';

  if (message.role === 'system') {
    return (
      <article
        role="article"
        aria-label={`system message at ${time}`}
        data-testid="msg-system"
        data-role="system"
        className="text-sm italic text-taupe-700"
      >
        <span className="font-semibold">system · </span>
        {message.content}
      </article>
    );
  }

  if (message.role === 'user') {
    return (
      <article
        role="article"
        aria-label={`user message at ${time}`}
        data-testid="msg-user"
        data-role="user"
        className="group relative ml-auto flex max-w-[75%] flex-col items-end"
      >
        <div className="mb-1 text-xs text-taupe-700">user · {time}</div>
        <div className="border-taupe-500 bg-cream-50 rounded-none border-l-2 p-3 text-sm">
          <div className="whitespace-pre-wrap" data-testid="msg-user-body">
            {message.content}
          </div>
        </div>
        {deleteAffordanceVisible ? (
          <MessageDeleteButton onClick={handleDelete} />
        ) : null}
      </article>
    );
  }

  // assistant
  return (
    <article
      role="article"
      aria-label={`assistant message at ${time}`}
      aria-busy={isStreaming || undefined}
      data-testid="msg-assistant"
      data-role="assistant"
      className="group relative flex flex-col"
    >
      <div className="mb-1 text-xs text-taupe-700">
        assistant · {time}
        {isStreaming ? <span data-testid="streaming-suffix"> (streaming ▎)</span> : null}
      </div>
      <AssistantBody
        content={message.content}
        isStreaming={isStreaming}
        onOpenSource={onOpenSource}
      />
      <ToolCallGroup
        entries={message.toolCalls ?? []}
        onRetry={onRetryToolCall}
        onRerun={onRerunToolCall}
        openInSyside={openInSyside}
      />

      <div data-testid="inline-cards-slot" className="flex flex-col gap-2">
        {/* Cards render in sort order: the deterministic React-Flow diagram
            (`response-view-proposal`, priority 1) sits ABOVE the AI raster
            (`illustrative-image`, priority 1.5), so the AI image always shows
            BELOW the diagram rather than replacing it. Each slot is wrapped in
            an error boundary so a single card's render throw can never
            white-screen the whole SPA. */}
        {sortInlineCards(inlineCards ?? []).map((envelope, idx) => (
          <InlineCardErrorBoundary
            // Use `cardIdentityKey`-style keying so SSE replays don't
            // remount cards and reset their ephemeral state. The envelope
            // discriminator is unique per (kind, primary-id), which the
            // inline-cards slice already guarantees via `attachCard`'s
            // idempotent append.
            key={`${envelope.kind}-${idx}`}
            cardKind={envelope.kind}
          >
            <InlineCardSlot
              envelope={envelope}
              handlers={inlineCardHandlers ?? {}}
              reducedMotion={reducedMotion}
            />
          </InlineCardErrorBoundary>
        ))}
      </div>
      {deleteAffordanceVisible ? (
        <MessageDeleteButton onClick={handleDelete} />
      ) : null}
    </article>
  );
}

function MessageDeleteButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      data-testid="msg-delete-button"
      aria-label="Delete message"
      title="Delete message"
      onClick={onClick}
      className="absolute right-1 top-1 inline-flex h-6 w-6 items-center justify-center rounded text-taupe-700 opacity-0 transition-opacity hover:bg-cream-200 hover:text-accent-red focus:opacity-100 group-hover:opacity-100"
    >
      <Trash2 size={14} />
    </button>
  );
}

function AssistantBody({
  content,
  isStreaming,
  onOpenSource,
}: {
  content: string;
  isStreaming: boolean;
  onOpenSource?: (filePath: string, elementName?: string) => void;
}): JSX.Element {
  const html = useMemo(() => renderMarkdown(content), [content]);

  // Event-delegated click handler for KB citation pills inside the sanitized
  // HTML. `closest('[data-file]')` walks up from the click target so nested
  // children inside the pill still resolve to the outer span.
  const dispatchSource = useCallback(
    (target: EventTarget | null): boolean => {
      if (onOpenSource === undefined) return false;
      if (!(target instanceof HTMLElement)) return false;
      const pill = target.closest('[data-file]') as HTMLElement | null;
      if (pill === null) return false;
      const filePath = pill.getAttribute('data-file');
      if (filePath === null || filePath.length === 0) return false;
      const elementName = pill.getAttribute('data-element') ?? undefined;
      onOpenSource(filePath, elementName);
      return true;
    },
    [onOpenSource],
  );

  const onClick = useCallback(
    (event: MouseEvent<HTMLDivElement>): void => {
      if (dispatchSource(event.target)) event.preventDefault();
    },
    [dispatchSource],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (dispatchSource(event.target)) event.preventDefault();
    },
    [dispatchSource],
  );

  return (
    <div className="text-fg text-sm leading-relaxed">
      <div
        data-testid="msg-assistant-body"
        className="chat-markdown"
        onClick={onClick}
        onKeyDown={onKeyDown}
        // dangerouslySetInnerHTML — DOMPurify sanitizes upstream; safe.
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {isStreaming ? (
        <span
          data-testid="streaming-cursor"
          aria-hidden="true"
          className="bg-audi-red ml-1 inline-block h-[12px] w-[2px] align-middle motion-safe:animate-pulse motion-reduce:animate-none"
        />
      ) : null}
    </div>
  );
}
